/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Environment, ENVIRONMENT_CONFIG } from '@common/environment-shared';
import { getUserName, getUserNameInitials, UserProfileResource } from '@alfa-client/user-profile-shared';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { AuthenticationConfig } from './authentication.model';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly oAuthService = inject(OAuthService);
  private readonly router = inject(Router);
  private readonly envConfig = inject<Environment>(ENVIRONMENT_CONFIG);

  currentUserResource: UserProfileResource;

  private eventSubscription: Subscription;

  public async login(config: AuthenticationConfig): Promise<void> {
    if (config.waitForInitialNavgiationToComplete) await this._initialNavigationIsDone();
    this.oAuthService.configure(this.buildConfiguration(config.redirectUriSuffix));
    this.oAuthService.setupAutomaticSilentRefresh();

    const eventPromise: Promise<void> = this.buildAuthEventPromise();
    await this.oAuthService.loadDiscoveryDocumentAndLogin();
    return eventPromise;
  }

  async _initialNavigationIsDone(): Promise<void> {
    await firstValueFrom(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)));
  }

  buildAuthEventPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => this.handleAuthEventsForPromise(resolve, reject));
  }

  private handleAuthEventsForPromise(resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void): void {
    this.eventSubscription = this.oAuthService.events
      .pipe(filter((event: OAuthEvent) => this.shouldProceedByAuthEvent(event)))
      .subscribe({
        next: () => {
          this.setCurrentUser();
          this.unsubscribeEvents();
          resolve();
        },
        error: (error: any) => {
          this.unsubscribeEvents();
          reject(error);
        },
      });
  }

  shouldProceedByAuthEvent(event: OAuthEvent): boolean {
    return this.consideredAsLoginEvent(event.type) || this.consideredAsPageReloadEvent(event.type);
  }

  consideredAsLoginEvent(eventType: string): boolean {
    return eventType === 'token_received';
  }

  consideredAsPageReloadEvent(eventType: string): boolean {
    return eventType === 'discovery_document_loaded' && this.hasValidToken();
  }

  hasValidToken(): boolean {
    return this.oAuthService.hasValidAccessToken() && this.oAuthService.hasValidIdToken();
  }

  buildConfiguration(redirectUriSuffix: string): AuthConfig {
    const realmBaseUrl = `${this.envConfig.authServer}/realms/${this.envConfig.realm}`;
    return {
      issuer: realmBaseUrl,
      tokenEndpoint: `${realmBaseUrl}/protocol/openid-connect/token`,
      logoutUrl: `${realmBaseUrl}/protocol/openid-connect/logout`,
      redirectUri: window.location.origin + redirectUriSuffix,
      clientId: this.envConfig.clientId,
      scope: 'openid profile',
      requireHttps: false,
      responseType: 'code',
      showDebugInformation: false,
      postLogoutRedirectUri: `${window.location.origin}/`,
    };
  }

  setCurrentUser(): void {
    const claims: Record<string, any> = this.oAuthService.getIdentityClaims();
    const userResource: UserProfileResource = <any>{
      firstName: claims['given_name'],
      lastName: claims['family_name'],
    };
    this.currentUserResource = userResource;
  }

  unsubscribeEvents(): void {
    this.eventSubscription.unsubscribe();
  }

  public getCurrentUserInitials(): string {
    return getUserNameInitials(this.currentUserResource);
  }

  public getCurrentUserName(): string {
    return getUserName(this.currentUserResource);
  }

  public logout(): void {
    this.oAuthService.logOut();
  }

  public isLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }
}
